<template>
  <data-table
    ref="datatableRef"
    :config="datatableConfig"
    :data="leads"
    :autoLoad="false"
    :refreshButton="refreshButtonConfig"
    @refresh="loadData"
    @edit-click="handleEditClick"
    @button-click="handleButtonClick">
  </data-table>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useSiteLeads } from '../../composables/useSiteLeads';
import { useDateFormatter } from '../../composables/useDateFormatter';
import DataTable from '../../grid/DataTable.vue';
import type { SiteLeads_LeadRow } from '../../contracts/SiteLeads_LeadRow';
import type { SiteLeads_ListaInput } from '../../contracts/SiteLeads_ListaInput';

type LeadId = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface CellParams {
  value: unknown;
  data?: SiteLeads_LeadRow;
}

interface ButtonClickData {
  action: string;
  data: SiteLeads_LeadRow;
}

const props = withDefaults(defineProps<{
  filtros?: SiteLeads_ListaInput;
}>(), {
  filtros: () => ({})
});

const emit = defineEmits<{
  view: [leadId: LeadId];
  /** The `formulario` slugs present in the page just loaded — the Page turns
   *  them into filter options, because the form list belongs to the site's
   *  tenant pack and this service must not hold a second copy of it. */
  formularios: [slugs: string[]];
}>();

const { t } = useI18n();
const { fetchLeads } = useSiteLeads();
const { formatDateTime, fromApiDateTimeFormat } = useDateFormatter();

const datatableRef = ref<DataTableInstance | null>(null);
const leads = ref<SiteLeads_LeadRow[]>([]);

/** cellRenderer output is v-html'd by DataTable — visitor text must be escaped. */
const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * `resumo` and `campanha` are NESTED, and DataTable reads a column by a flat
 * key (`row[column.data]`) — so every nested value goes through a cellRenderer
 * reading `params.data`, never through a dotted `data:` path.
 */
const textoDaLinha = (valor: unknown): string => {
  const texto = typeof valor === 'string' ? valor.trim() : '';
  return escapeHtml(texto);
};

const tableColumns = computed(() => {
  return [
    {
      data: 'id',
      title: t('ID'),
      width: 90,
      sortable: true,
      filterType: 'text',
      renderType: 'clickable_id'
    },
    {
      data: 'recebido_em',
      title: t('Recebido em'),
      width: 170,
      sortable: true,
      cellRenderer: (params: CellParams): string => {
        const iso = params.value as string | undefined;
        if (!iso) return '';
        return escapeHtml(formatDateTime(fromApiDateTimeFormat(iso)) || '');
      }
    },
    {
      data: 'formulario',
      title: t('Formulário'),
      width: 160,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'resumo',
      title: t('Nome'),
      width: 200,
      sortable: false,
      cellRenderer: (params: CellParams): string => textoDaLinha(params.data?.resumo?.nome)
    },
    {
      data: 'resumo',
      title: t('E-mail'),
      width: 220,
      sortable: false,
      cellRenderer: (params: CellParams): string => textoDaLinha(params.data?.resumo?.email)
    },
    {
      data: 'resumo',
      title: t('Telefone'),
      width: 150,
      sortable: false,
      cellRenderer: (params: CellParams): string => textoDaLinha(params.data?.resumo?.telefone)
    },
    {
      data: 'payload',
      title: t('Origem da campanha'),
      width: 170,
      sortable: false,
      cellRenderer: (params: CellParams): string => {
        const campanha = params.data?.payload?.campanha as Record<string, unknown> | undefined;
        return textoDaLinha(campanha?.utm_source);
      }
    },
    {
      data: 'id',
      title: t('Detalhes'),
      orderable: false,
      renderType: 'clickable_button',
      buttonText: t('Detalhes'),
      buttonIcon: 'pi pi-search',
      buttonClass: 'p-button-sm p-button-outlined',
      buttonAction: 'detail'
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    filename: `leads_site_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    rowTestId: (data: Record<string, unknown>) => {
      const id = data?.id;
      return id == null ? '' : `site-leads-row-${id}`;
    },
    pageLength: 25,
    pagination: true,
    rowSelection: 'single',
    sortable: true,
    resizableColumns: true,
    showFilters: true,
    filterDisplay: 'row',
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const loadData = async (): Promise<void> => {
  log('SiteLeadsTable', 'loadData', 'Loading leads', props.filtros);
  try {
    const page = await fetchLeads({
      formulario: props.filtros.formulario || undefined,
      de: props.filtros.de || undefined,
      ate: props.filtros.ate || undefined
    });
    leads.value = page.items || [];
    log('SiteLeadsTable', 'loadData', 'Loaded leads', leads.value.length);

    const slugs = Array.from(new Set(
      leads.value
        .map((lead) => (lead.formulario ?? '').trim())
        .filter((slug) => slug !== '')
    ));
    emit('formularios', slugs);
  } catch (err) {
    const error = err as Error;
    log('SiteLeadsTable', 'loadData', 'Error loading leads', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar leads') + ': ' + error.message);
  }
};

const handleEditClick = (leadId: LeadId): void => {
  log('SiteLeadsTable', 'handleEditClick', 'Row id clicked', leadId);
  emit('view', leadId);
};

const handleButtonClick = (buttonData: ButtonClickData): void => {
  log('SiteLeadsTable', 'handleButtonClick', 'Button clicked', buttonData);
  const id = buttonData.data?.id;
  if (buttonData.action === 'detail' && id !== undefined) {
    emit('view', id);
  }
};

watch(() => props.filtros, () => {
  void loadData();
}, { deep: true });

onMounted(() => {
  log('SiteLeadsTable', 'mounted', 'Loading first page');
  void loadData();
});

defineExpose({
  loadData
});
</script>

<style scoped>
</style>
